<template>
    <div>
        <q-expansion-item 
                :group="expansionGroup" 
                class="report-expansion-item" 
                expand-icon="mdi-menu-down" 
                header-class="text-h6" 
                header-style="height: 64px;" 
                style="border-bottom: 1px solid var(--v-lightGreyBorder-base);" 
                :label="Translate('Metrics')">

            <div class="q-mt-none consistentText q-pb-sm">
                {{ Translate('Select the metrics to include in the report. Use the search box or the filter icon to narrow the choices.') }}
            </div>

            <OverflowPillGroup
                class="q-py-none"
                :removePill="removeMetric"
                :pills="metrics"
                :typeDescriptor="Translate('metrics')"
                @reorder="onMetricsReorder"
                >
            </OverflowPillGroup>


            <div class="row items-center no-wrap q-py-sm">
                <q-input outlined dense hide-bottom-space clearable :label="Translate('Search')" v-model="search_metrics" class="_standard-input-field col" bg-color="white">
                    <template v-slot:prepend>
                        <q-icon name="mdi-magnify" />
                    </template>
                </q-input>
                <q-btn dense flat icon="mdi-filter" style="color: var(--v-secondaryText-base);" class="col-auto q-ml-sm filter-action-btn">
                    <q-badge v-if="anyMetricfilters" color="primary" floating rounded dot />
                    <q-menu
                        anchor="bottom right"
                        self="top right"
                        :no-parent-event="false"
                        :close-on-content-click="false"
                        v-model="metric_filter_menuopen"
                        style="overflow: hidden;"
                    >
                        <q-card @mouseleave="metric_filter_menuopen = false" class="q-pa-xs" style="min-width: 400px; max-height: calc(65vh - 8px); overflow: hidden;">
                            <MetricTypeFilter :debugMode="debugMode"
                                                :datatypes="metricDataTypes"
                                                :initial_datatypes_selection="metricDataTypes_selection"
                                                @datatypes_changed="(values) => metricDataTypes_selection = values"
                                                :categories="metricCategories"
                                                :initial_category_selection="metricCategory_selection"
                                                @categories_changed="(values) => metricCategory_selection = values"
                                                :sources="metricSources"
                                                :initial_sources_selection="metricSources_selection"
                                                @sources_changed="(values) => metricSources_selection = values"
                                                @anyfilters="(value) => anyMetricfilters = value">
                            </MetricTypeFilter>
                        </q-card>
                    </q-menu>
                </q-btn>
            </div>

            <div v-if="search_metrics" class="q-mt-sm">
                <div style="border-bottom: 1px solid var(--v-lightGreyBorder-base); color: #9d9d9d;" class="q-mb-none q-pb-none q-px-sm">
                    {{ Translate('SEARCH RESULTS') }}
                </div>

                <MetricItemList :items="metricsearchresults"
                                :columnLookup="columnLookup"
                                :isMetricInUse="isMetricInUse"
                                :getMetricItemIcon="getMetricItemIcon"
                                :showHelp="showHelp"
                                :isDisabledMetric="isDisabledMetric"
                                :isDisabledSource="isMetricDisabledSource"
                                :addMetric="addMetric"
                                :removeMetric="removeMetric"
                                :getMetricHelp="getMetricHelp"
                                :metric_types="metric_types"></MetricItemList>

            </div>

            <div v-else v-for="(group,i) in metricGroups" :key="i">
                <div v-if="isMetricGroupVisible(group)"
                                    style="border-bottom: 1px solid var(--v-lightGreyBorder-base);" class="q-mb-none q-pb-none consistentText q-px-sm q-mt-md">{{ Translate(group.text).toUpperCase() }}</div>

                <div v-if="isMetricGroupVisible(group)">
                    <MetricItemList :items="getMetricColumns(group)"
                                    :columnLookup="columnLookup"
                                    :isMetricInUse="isMetricInUse"
                                    :getMetricItemIcon="getMetricItemIcon"
                                    :showHelp="showHelp"
                                    :isDisabledMetric="isDisabledMetric"
                                    :isDisabledSource="isMetricDisabledSource"
                                    :addMetric="addMetric"
                                    :removeMetric="removeMetric"
                                    :getMetricHelp="getMetricHelp"
                                    :metric_types="metric_types"></MetricItemList>
                </div>
            </div>
        </q-expansion-item>

        <q-expansion-item 
                :group="expansionGroup" 
                class="report-expansion-item" 
                expand-icon="mdi-menu-down" 
                header-class="text-h6" 
                header-style="height: 64px;" 
                style="border-bottom: 1px solid var(--v-lightGreyBorder-base);" 
                :label="Translate('Groupings')">
            <div class="q-mt-none consistentText q-pb-sm">
                {{ Translate('Choose the fields you want to group report data by (maximum permitted is four). Remember that only one Date/Time field can be chosen.') }}
            </div>

            <div class="consistentText q-mt-none q-pt-none">
                {{ Translate('Drag and drop the fields already selected to change their order.') }}
            </div>

            <div class="subtitle-1 q-mt-md" style="overflow: auto; display: flex; align-items: center;">
                <div class="q-ma-none q-pa-none consistentText">
                    {{ Translate('Grouping order:') }}
                </div>
                <Container @drop="onDrop" orientation="horizontal" style="margin-left: 20px;">
                    <Draggable v-for="(c,i) in groupby" :key="c" :index="i">
                        <q-chip class="draggable-item" color="primary" outline>
                             {{ Translate(groupColumnLookup[c].Title) }}
                        </q-chip>
                    </Draggable>
                </Container>
            </div>

            <div class="row items-center no-wrap q-py-sm">
                <q-input outlined dense hide-bottom-space clearable :label="Translate('Search')" v-model="search_groups" class="_standard-input-field col" bg-color="white">
                    <template v-slot:prepend>
                        <q-icon name="mdi-magnify" />
                    </template>
                </q-input>
                <q-btn dense flat icon="mdi-filter" style="color: var(--v-secondaryText-base);" class="col-auto q-ml-sm filter-action-btn">
                    <q-badge v-if="anyGroupfilters" color="primary" floating rounded dot />
                    <q-menu
                        anchor="bottom right"
                        self="top right"
                        :close-on-content-click="false"
                        v-model="group_filter_menuopen"
                        style="overflow: hidden;"
                    >
                        <q-card @mouseleave="group_filter_menuopen = false" class="q-pa-xs" style="min-width: 400px; max-height: calc(65vh - 8px); overflow: hidden;">
                            <MetricTypeFilter :debugMode="debugMode"
                                                :datatypes="groupDataTypes"
                                                :initial_datatypes_selection="groupDataTypes_selection"
                                                @datatypes_changed="(values) => groupDataTypes_selection = values"
                                                :categories="groupCategories"
                                                :initial_category_selection="groupCategory_selection"
                                                @categories_changed="(values) => groupCategory_selection = values"
                                                :sources="groupSources"
                                                :initial_sources_selection="groupSources_selection"
                                                @sources_changed="(values) => groupSources_selection = values"
                                                @anyfilters="(value) => anyGroupfilters = value">
                            </MetricTypeFilter>
                        </q-card>
                    </q-menu>
                </q-btn>
            </div>

            <div class="row items-center q-py-sm" v-if="getPivotOn">
                <q-btn
                    flat
                    dense
                    no-caps
                    class="q-mr-md q-mb-none q-py-none text-weight-regular subtitle-1"
                    style="line-height: normal;"
                    @click="pivoton = !pivoton"
                >
                    <q-icon
                        class="q-mr-xs"
                        style="font-size: 18px;"
                        :name="pivoton ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"
                        :color="pivoton ? 'primary' : undefined"
                    />
                    <span class="consistentText">{{ Translate('Pivot on') + ' ' + Translate(getPivotOn.Title) }}</span>
                </q-btn>

                <q-icon name="mdi-information" size="xs" class="cursor-pointer form-hint-icon">
                    <q-tooltip anchor="bottom middle" self="top middle" :delay="50" transition-show="scale" transition-hide="scale">
                        <span style="font-size: larger;">
                            {{ Translate('Pivoting is available for the last column in your group list. You can rearrange grouping columns by dragging and dropping them in the pills list above. Pivoting generates a distinct column for every unique value within each row.') }}
                        </span>
                    </q-tooltip>
                </q-icon>
            </div>

            <div v-if="!search_groups" class="q-mt-sm">
                <div class="q-mb-none q-pb-none consistentText q-px-sm" style="border-bottom: 2px solid lightgrey;">
                    <span style="display: flex; align-items: flex-end;" class="q-mb-xs">
                        {{ Translate('DATE / TIME') }}
                        <q-space />
                        <q-btn dense round flat icon="mdi-cog-outline" style="color: var(--v-secondaryText-base);" class="q-ma-none">
                            <q-menu anchor="bottom left" self="top left" :close-on-content-click="false">
                            <q-list dense>
                                <q-item dense class="q-py-xs">
                                    <q-item-section class="q-pl-none q-pr-none">
                                        <q-toggle dense class="q-ma-none q-pt-none no-wrap date-time-toggle" :label="Translate('Show all intervals')" v-model="all_intervals" />
                                    </q-item-section>
                                    <q-item-section side class="q-pl-sm q-pr-md">
                                        <q-icon name="mdi-information" size="xs" class="cursor-pointer form-hint-icon">
                                            <q-tooltip anchor="bottom middle" self="top middle" :delay="50" transition-show="scale" transition-hide="scale">
                                            <span style="font-size: larger;">
                                            {{ Translate('(Beta) Enable to include all time intervals in the report, even those where there was no activity.') }}
                                            {{ Translate('If you group by Date/Time and other metrics, the report might include blank rows (when some data points don\'t have values for every grouping).') }}
                                            {{ Translate('To avoid blank rows, group by Date/Time only.') }}
                                            </span>
                                            </q-tooltip>
                                        </q-icon>
                                    </q-item-section>
                                </q-item>
                                <q-item dense class="q-py-xs">
                                    <q-item-section class="q-pl-none q-pr-none">
                                        <q-toggle dense class="q-ma-none q-pt-none no-wrap date-time-toggle" :label="Translate('Show start/end range')" v-model="show_interval_range" />
                                    </q-item-section>
                                    <q-item-section side class="q-pl-sm q-pr-md">
                                        <q-icon name="mdi-information" size="xs" class="cursor-pointer form-hint-icon">
                                            <q-tooltip anchor="bottom middle" self="top middle" :delay="50" transition-show="scale" transition-hide="scale">
                                            <span style="font-size: larger;">
                                            {{ Translate('Show the start and end range for each date/time grouping') }}
                                            </span>
                                            </q-tooltip>
                                        </q-icon>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                            </q-menu>
                        </q-btn>
                    </span>
                </div>
                <div>
                    <GroupbyItemList :items="[defaultTimeColumn]"
                                        :groupbysettings="groupbysettings"
                                        :isMetricInUse="(g) => groupby.includes(g.ColId)"
                                        :showHelp="showHelp"
                                        :isDisabledSource="isGroupDisabledSource"
                                        :toggleGroupby="toggleGroupby"
                                        :getMetricHelp="getMetricHelp"
                                        :metric_types="metric_types"
                                        :interval_choice="interval_choice"
                                        @settingschanged="setgroupbysettings"
                                        @intervalchanged="(i) => interval_choice = i"></GroupbyItemList>
                </div>
            </div>

            <div v-if="search_groups" class="q-mt-sm">
                <div style="border-bottom: 2px solid lightgrey;" class="q-mb-none q-pb-none consistentText q-px-sm">
                    {{ Translate('SEARCH RESULTS') }}
                </div>

                <div>
                    <GroupbyItemList :items="groupsearchresults"
                                        :groupbysettings="groupbysettings"
                                        :isMetricInUse="(g) => groupby.includes(g.ColId)"
                                        :showHelp="showHelp"
                                        :isDisabledSource="isGroupDisabledSource"
                                        :toggleGroupby="toggleGroupby"
                                        :getMetricHelp="getMetricHelp"
                                        :refreshReport="refreshReport"
                                        :metric_types="metric_types"
                                        @settingschanged="setgroupbysettings"></GroupbyItemList>
                </div>
            </div>

            <div v-for="(group,i) in groupingGroups" :key="i" class="q-mt-sm">
                <div v-if="isGroupVisible(group)"
                                    style="border-bottom: 2px solid lightgrey;" class="q-mb-none q-pb-none q-mt-md consistentText q-px-sm">
                     {{ Translate(group.text).toUpperCase() }}
                </div>

                <div v-if="isGroupVisible(group)">
                    <GroupbyItemList :items="getGroupColumns(group)"
                                        :groupbysettings="groupbysettings"
                                        :isMetricInUse="(g) => groupby.includes(g.ColId)"
                                        :showHelp="showHelp"
                                        :isDisabledSource="isGroupDisabledSource"
                                        :toggleGroupby="toggleGroupby"
                                        :getMetricHelp="getMetricHelp"
                                        :refreshReport="refreshReport"
                                        :metric_types="metric_types"
                                        @settingschanged="setgroupbysettings"></GroupbyItemList>
                </div>
            </div>

        </q-expansion-item>

        <q-dialog v-model="help.visible">
            <q-card style="max-width: 590px; width: 100%;">
                <q-card-section class="text-h5">
                    {{ help.item ? Translate(help.item.Title) : '' }}
                </q-card-section>

                <q-card-section v-html="help.item.Description">

                </q-card-section>

                <q-card-actions align="right">
                    <q-space />

                    <q-btn color="primary"
                           flat
                           no-caps
                           class="c-SentenceCaseButton"
                           @click="help.visible = false">
                        {{ Translate('Close') }}
                    </q-btn>
                </q-card-actions>
            </q-card>
        </q-dialog>

        <q-dialog v-model="filter.visible">
            <q-card style="max-width: 590px; width: 100%;">
                <q-card-section class="text-h5">
                    {{ Translate('Filter for') }} {{ filter.item.calculation }}({{ Translate(filter.item.Title) }})
                </q-card-section>

                <q-card-section>
                    {{ Translate('This applies a filter to the metric calculation to control which records are included. You may use the same column multiple times with different filters if you like.') }}

                    <q-input dense outlined hide-bottom-space
                                  :label="Translate('Name')"
                                  class="q-mt-xl q-mb-xl"
                                  v-model="filter.item.name"
                                  bg-color="white">
                    </q-input>

                    {{ Translate('Imagine the filter settings right here.') }}
                </q-card-section>

                <q-card-actions align="right">
                    <q-space />

                    <q-btn color="positive"
                           flat
                           no-caps
                           class="c-SentenceCaseButton"
                           @click="filter.visible = false">
                        {{ Translate('Save') }}
                    </q-btn>

                    <q-btn color="positive"
                           flat
                           no-caps
                           class="c-SentenceCaseButton"
                           @click="filter.visible = false">
                        {{ Translate('Cancel') }}
                    </q-btn>
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
    /* eslint-disable */

    import helps from '../../../careHelpfulFunctions.jsx';

    import { Container, Draggable } from 'vue3-smooth-dnd';
    import MenuColumnGroups from './menuColumnGroups.vue';
    import PopupMenuSelection from './menuIntervalSelection.vue';
    import CardHistogram from './cardHistogram.vue';
    import MetricItem from './metricItem.vue';
    import MetricItemList from './metricItemList.vue';
    import GroupbyItemList from './groupbyItemList.vue';
    import MetricTypeFilter from './metricTypeFilterPopup.vue';
    import OverflowPillGroup from './overflowPillGroup.vue';
    import methods from '@/Shared/methods';

    import {
        QExpansionItem, QInput, QSelect, QIcon, QBtn, QTooltip, QMenu, QCard, QCardSection, QCardActions,
        QList, QItem, QItemSection, QSeparator, QToggle, QCheckbox, QDialog, QSpace, QChip, QBanner, QBadge,
    } from 'quasar';

    import { appSettings } from '@/Shared/appSettings';

    import common from './common.js';

    const groupCols = ['dateColumn', 'textSetColumn', 'channelTypeColumn', 'directionColumn', 'userIDColumn'];
    const metricCols = ['durationColumn', 'countColumn'];

    export default {
        components: {
            Container,
            Draggable,
            MenuColumnGroups,
            PopupMenuSelection,
            CardHistogram,
            MetricItem,
            MetricItemList,
            GroupbyItemList,
            MetricTypeFilter,
            QExpansionItem, QInput, QSelect, QIcon, QBtn, QTooltip, QMenu, QCard, QCardSection, QCardActions,
            QList, QItem, QItemSection, QSeparator, QToggle, QCheckbox, QDialog, QSpace, QChip, QBanner, QBadge,
            OverflowPillGroup,
        },
        props: {
            expansionGroup: {
                type: String,
                required: true,
            },
            column_list: {
                type: Array,
                required: false
            },
            settings: null,
            report_settings: null,
            initial_userStartEnd: null,
            storageKey: null,
            datetime_interval: null,
            notifychange: null,
            debugMode: null,
        },
        data: function () {
            return {
                greeting: 'Hello',
                changing: 0,
                useStartEnd: true,
                groupby: ['datetime'],
                groupbysettings: {},
                all_intervals: false,
                show_interval_range: false,
                pivoton: false,
                search_metrics: '',
                search_groups: '',
                interval_choice: '',
                interval_selection: 2,
                defaultTimeColumn: {
                    ColId: 'datetime',
                    Title: 'Date / Time',
                    Tooltip: 'Date and time based on the start of the interaction or state',
                    Description: 'This is the general date and time column. When used for grouping, you must choose the interval. The actual field used for the grouping may be determined based on the metric(s) selected. The default is the starttime field.',
                    Category: 'Dates & Times',
                    SourceType: 'dateColumn'
                },
                active_interval: '1h',
                defaultTime: 'today',
                advancedVisible: undefined,

                formatHeader: common.formatHeader,

                metricDataTypes: [
                    { text: 'Counts', filter: 'type', types: ['countColumn', 'floatColumn'] },
                    { text: 'Dates / Times', filter: 'type', types: ['dateColumn', 'dateOnlyColumn', 'timeOnlyColumn'] },
                    { text: 'Durations', filter: 'type', types: ['durationColumn'] },
                    { text: 'Percents', filter: 'type', types: ['percentColumn'] },
                    { text: 'Text Values', filter: 'type', types: ['textSetColumn', 'searchableTextColumn', 'channelTypeColumn', 'directionColumn', 'userIDColumn', 'customerIDColumn', 'phoneNumColumn'] },
                    { text: 'True / False', filter: 'type', types: ['booleanColumn'] },
                ],
                metricCategories: [],
                metricSources: [],

                anyMetricfilters: false,

                metricDataTypes_selection: [],
                metricCategory_selection: [],
                metricSources_selection: [],

                groupDataTypes: [
                    { text: 'Durations', filter: 'type', types: ['durationColumn'] }, //0
                    { text: 'Text Values', filter: 'type', types: ['textSetColumn', 'searchableTextColumn', 'channelTypeColumn', 'directionColumn', 'userIDColumn', 'customerIDColumn', 'phoneNumColumn'] }, //1
                ],
                groupCategories: [],
                groupSources: [],

                anyGroupfilters: false,

                groupDataTypes_selection: [],
                groupCategory_selection: [],
                groupSources_selection: [],

                metricgroup_expanded: [0],
                columnmetrics_selection: [],

                metric_types: {
                    avg: { text: 'Average', icon: 'mdi-calculator' },
                    max: { text: 'Longest', icon: 'mdi-code-greater-than' },
                    min: { text: 'Shortest', icon: 'mdi-code-less-than' },
                    sum: { text: 'Sum', icon: 'mdi-sigma' },
                    cardinality: { text: 'Cardinality', icon: 'mdi-counter' },
                    value_count: { text: 'Hit Count', icon: 'mdi-counter' },
                    bucket: { text: 'Percent', icon: 'mdi-percent-circle-outine' },
                },
                metrics: [],
                metricMenu: [],
                metric_filter_menuopen: false,
                group_filter_menuopen: false,

                group_col_settings_open: false,
                help: {
                    visible: false,
                    item: {}
                },
                filter: {
                    visible: false,
                    item: {}
                },
                isReorderingMetrics: false,
                isReorderingGroups: false,
            }
        },
        watch: {
            metricDataTypes_selection: function (newv) {
                this.saveMetricFilters();
            },
            metricCategory_selection: function (newv) {
                this.saveMetricFilters();
            },
            metricSources_selection: function (newv) {
                this.saveMetricFilters();
            },
            groupDataTypes_selection: function (newv) {
                this.saveGroupFilters();
            },
            groupCategory_selection: function (newv) {
                this.saveGroupFilters();
            },
            groupSources_selection: function (newv) {
                this.saveGroupFilters();
            },

            groupby: {
                handler() {
                    if (this.isReorderingGroups) {
                        this.isReorderingGroups = false;
                        return;
                    }
                    this.notifyGroupOrder();
                    this.refreshReport();
                },
                deep: true,
            },
            'report_settings.metrics': {
                handler(newMetrics) {
                    if (!newMetrics) return;

                    const mappedNew = newMetrics.map(a => ({
                        id: `${a.function}(${a.colId})`,
                        calculation: a.function,
                        ...this.columnLookup[a.colId],
                    }));
                    
                    const currentIds = this.metrics.map(m => m.id).join(',');
                    const newIds = mappedNew.map(m => m.id).join(',');
                    if (currentIds !== newIds) {
                        this.isReorderingMetrics = true;
                        this.metrics = mappedNew;
                    }
                }
            },
            'report_settings.groupby': {
                handler(newGroupby) {
                    if (!newGroupby) return;

                    const mappedNew = newGroupby.map(a => a.colId);

                    // Pivot column is omitted from report_settings.groupby but kept in the UI groupby list.
                    if (this.report_settings.pivoton)
                        mappedNew.push(this.report_settings.pivoton);

                    const currentIds = this.groupby.join(',');
                    const newIds = mappedNew.join(',');
                    
                    if (currentIds !== newIds) {
                        this.isReorderingGroups = true;
                        this.groupby = mappedNew;
                    }

                    this.pivoton = !!this.report_settings.pivoton;
                }
            },
            metrics: {
                handler() {
                    if (this.isReorderingMetrics) {
                        this.isReorderingMetrics = false;
                        return;
                    }
                    this.refreshReport();
                },
                deep: true,
            },
            pivoton: {
                handler() {
                    this.refreshReport();
                },
                deep: true,
            },
            getPivotOn: function (newv, oldv) {
                if (newv !== oldv && !newv && this.pivoton)
                    this.pivoton = false;
            },
            interval_choice: function (newv) {
                appSettings.LocalStorage(this.storageKey + '-interval', newv);
                this.refreshReport();
            },
            columngroups_selection: function (newv) {
                appSettings.LocalStorage(this.storageKey + '-groupcategories', JSON.stringify(newv));
            },
            columnmetrics_selection: function (newv) {
                appSettings.LocalStorage(this.storageKey + '-metriccategories', JSON.stringify(newv));
            },
            useStartEnd: function (newv) {
                this.$emit('changeusestartend', newv);
            },

            all_intervals: function(newv){

                    this.groupbysettings[this.defaultTimeColumn.ColId] = { ...this.groupbysettings[this.defaultTimeColumn.ColId] ,colId: this.defaultTimeColumn.ColId, all_intervals: newv};
                    this.setgroupbysettings(this.groupbysettings);
            },
            show_interval_range: function(newv){
                
                    this.groupbysettings[this.defaultTimeColumn.ColId] = { ...this.groupbysettings[this.defaultTimeColumn.ColId], colId: this.defaultTimeColumn.ColId, show_interval_range: newv};
                    this.setgroupbysettings(this.groupbysettings);
            }            
        },
        created() {
            //if (this.datetime_interval)
            //    this.interval_choice = this.datetime_interval;

            this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });

            const oldinterval = appSettings.LocalStorage(this.storageKey + '-interval');
            if (oldinterval)
                this.interval_choice = oldinterval;
            else
                this.interval_choice = '1d';

            //------------------------ New --------------------//

            // Populate a distinct list of categories for metric filters
            this.metricCategories = [...new Set(this.column_list.filter(a => a.ColumnUse.Metric).map(a => a.Category))]
                .toSorted((a, b) => common.byField(a, b))
                .map(a => ({ text: a, filter: 'Category', category: a }));

            // Populate a distinct list of source names (contacts, users, etc)
            this.metricSources = [...new Set(this.column_list.filter(a => a.ColumnUse.Metric).flatMap(a => a.Sources))]
                .toSorted((a, b) => common.byField(common.sourceTypes[a], common.sourceTypes[b]))
                .map(a => ({ text: a, filter: 'Source', source: a }));

            // Populate a distinct list of categories for groupby filters
            this.groupCategories = [...new Set(this.column_list.filter(a => a.ColumnUse.Group).map(a => a.Category))]
                .toSorted((a, b) => common.byField(a, b))
                .map(a => ({ text: a, filter: 'Category', category: a }));

            // Populate a distinct list of source names (contacts, users, etc)
            this.groupSources = [...new Set(this.column_list.filter(a => a.ColumnUse.Group).flatMap(a => a.Sources))]
                .toSorted((a, b) => common.byField(common.sourceTypes[a], common.sourceTypes[b]))
                .map(a => ({ text: a, filter: 'Source', source: a }));

            this.loadMetricFilters();
            this.loadGroupFilters();
            //-------------------------------------------------//

            if (this.report_settings && this.report_settings.groupby) {
                console.log('created report fields, report_settings:');
                console.log(JSON.stringify(this.report_settings,null,3));

                this.groupby = this.report_settings.groupby.map(a => a.colId);
                this.groupbysettings = this.report_settings.groupby.reduce((p,c) => { p[c.colId] = c; return p }, {});

                if (this.report_settings.pivoton)
                    this.groupby.push(this.report_settings.pivoton);

                this.metrics = this.report_settings.metrics.map(a => ({
                    id: `${a.function}(${a.colId})`,
                    calculation: a.function,
                    ...this.column_list.find(c => c.ColId == a.colId),
                }));
                this.interval_choice = this.report_settings.datetime_interval;
                this.pivoton = !!this.report_settings.pivoton;
            }
            else
                console.log('created report fields, report_settings empty');

            this.useStartEnd = this.initial_userStartEnd;
            
            this.all_intervals = !!this.groupbysettings[this.defaultTimeColumn.ColId]?.all_intervals;
            this.show_interval_range = !!this.groupbysettings[this.defaultTimeColumn.ColId]?.show_interval_range;
        },
        mounted() {
        },
        computed: {
            groupColumnLookup: function() {
                return helps.toLookup(this.groupingColumns, 'ColId');
            },
            columnLookup: function() {
                return helps.toLookup(this.column_list, 'ColId');
            },
            groupingGroups: function() {
                return [...new Set(this.column_list.filter(a => a.ColumnUse.Group && a.SourceType != 'dateColumn').map(a => a.Category))]
                    .toSorted((a, b) => common.byField(a, b))
                    .map(a => ({ text: a, filter: 'Category', category: a }));
            },
            groupingColumns: function() {
                const cols = this.column_list.filter(a => a.ColumnUse.Group);
                return [{
                    ColId: 'datetime',
                    Title: 'Date / Time',
                    SourceType: 'dateColumn',
                    Sources: ['contacts', 'users', 'ivr'],
                    Category: 'Dates / Times',
                }, ...cols ];
            },
            metricGroups: function () {
                return [...new Set(this.column_list.filter(a => a.ColumnUse.Metric).map(a => a.Category))]
                    .toSorted((a, b) => common.byField(a, b))
                    .map(a => ({ text: a, filter: 'Category', category: a }));
            },
            
            groupsources_selected: function () {
                if (this.groupby.length < 1) return [];

                // Gets a distinct list of sources (users, contacts, etc) that are selected in the groupby fields
                // Need to find any columns that have only one or the other source. Ex:
                // starttime: [users, contacts]
                // team:      [users, contacts]
                // statename: [users]
                // This means, only users since statename doesn't have contacts.
                // We have to prevent selecting a groupby with one that is only users and one that is only contacts

                // Get all the Sources and sort the list by the length of the Sources list -- the lowest length is the one with the fewest sources.
                // That is the one we will return.

                const all = this.groupby.map(a => this.column_list.find(b => b.ColId == (a == 'datetime' ? 'starttime' : a))).map(a => a.Sources)
                    .toSorted((a, b) => a.length < b.length ? -1 : a.length > b.length ? 1 : 0);

                return all[0];
            },
            metricsources_selected: function () {
                if (this.metrics.length < 1) return [];

                // Return a distinct list of all sources from the selected metrics
                return [... new Set(this.metrics.flatMap(a => a.Sources))];
            },

            groupsearchresults: function () {
                const cols = this.column_list.filter(a => a.ColumnUse.Group && !a.CompoundFilter && a.SourceType != 'dateColumn');
                return cols.filter(a =>
                    a.ColId.toLowerCase().includes(this.search_groups.toLowerCase()) ||
                    a.Title.toLowerCase().includes(this.search_groups.toLowerCase()) ||
                    a.Tooltip?.toLowerCase().includes(this.search_groups.toLowerCase()) ||
                    a.Description?.toLowerCase().includes(this.search_groups.toLowerCase()) ||
                    a.SourceType?.toLowerCase().includes(this.search_groups.toLowerCase())
                ).toSorted((a, b) => this.byField(a, b, 'Title'));
            },
            metricsearchresults: function(){
                const cols = this.column_list.filter(a => a.ColumnUse.Metric && !a.CompoundFilter);
                return cols.filter(a =>
                    a.ColId.toLowerCase().includes(this.search_metrics.toLowerCase()) ||
                    a.Title.toLowerCase().includes(this.search_metrics.toLowerCase()) ||
                    a.Tooltip?.toLowerCase().includes(this.search_metrics.toLowerCase()) ||
                    a.Description?.toLowerCase().includes(this.search_metrics.toLowerCase()) ||
                    a.SourceType?.toLowerCase().includes(this.search_metrics.toLowerCase())
                ).toSorted((a, b) => this.byField(a, b, 'Title'));
            },

            disableDateGroupInterval: function() {
                return !this.groupingColumns.some(a => a.SourceType == 'dateColumn' && this.groupby.some(b => b == a.ColId));
            },
            selectedDateColumn: function() {
                const c = this.groupingColumns.find(a => a.SourceType == 'dateColumn' && this.groupby.some(b => b == a.ColId));
                if (c)
                    return c;
                else
                    return false;
            },

            getPivotOn: function() {
                console.log(`getPivotOn:`);
                console.log(JSON.stringify(this.groupby,null,3));

                if (this.groupby.length < 2)
                    return null;

                const groupby = this.groupby.map(g => this.groupingColumns.find(c => c.ColId == g));

                const g = groupby[groupby.length-1];
                if (g.SourceType != 'dateColumn')
                    return { ColId: g.ColId, Title: g.Title };

                return null;
            },
        },
        methods: {
            ...methods,
            loadMetricFilters() {
                for (let i = 0; i < this.metricDataTypes.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricDataTypes[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.metricDataTypes_selection.push(i);

                for (let i = 0; i < this.metricCategories.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricCategories[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.metricCategory_selection.push(i);

                if (this.debugMode) {
                    for (let i = 0; i < this.metricSources.length; i++)
                        if (appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricSources[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                            this.metricSources_selection.push(i);
                }
                else
                    this.metricSources_selection = this.metricSources.map((a, i) => i);

                this.anyMetricfilters =
                    this.metricDataTypes_selection.length < this.metricDataTypes.length ||
                    this.metricCategory_selection.length < this.metricCategories.length ||
                    this.metricSources_selection.length < this.metricSources.length;
            },
            loadGroupFilters() {
                for (let i = 0; i < this.groupDataTypes.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupDataTypes[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.groupDataTypes_selection.push(i);

                for (let i = 0; i < this.groupCategories.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupCategories[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.groupCategory_selection.push(i);

                if (this.debugMode) {
                    for (let i = 0; i < this.groupSources.length; i++)
                        if (appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupSources[i].text + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                            this.groupSources_selection.push(i);
                }
                else
                    this.groupSources_selection = this.groupSources.map((a, i) => i);

                this.anyGroupfilters =
                    this.groupDataTypes_selection.length < this.groupDataTypes.length ||
                    this.groupCategory_selection.length < this.groupCategories.length ||
                    this.groupSources_selection.length < this.groupSources.length;
            },
            saveMetricFilters() {
                for (let i = 0; i < this.metricDataTypes.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricDataTypes[i].text + '-' + (this.source ? `-${this.source}` : ''), this.metricDataTypes_selection.includes(i) ? null : 'hidden');

                for (let i = 0; i < this.metricCategories.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricCategories[i].text + '-' + (this.source ? `-${this.source}` : ''), this.metricCategory_selection.includes(i) ? null : 'hidden');

                if (this.metricSources)
                    for (let i = 0; i < this.metricSources.length; i++)
                        appSettings.LocalStorage(this.storageKey + '-metricFilters-type:' + this.metricSources[i].text + '-' + (this.source ? `-${this.source}` : ''), this.metricSources_selection.includes(i) ? null : 'hidden');
            },
            saveGroupFilters() {
                for (let i = 0; i < this.groupDataTypes.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupDataTypes[i].text + '-' + (this.source ? `-${this.source}` : ''), this.groupDataTypes_selection.includes(i) ? null : 'hidden');

                for (let i = 0; i < this.groupCategories.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupCategories[i].text + '-' + (this.source ? `-${this.source}` : ''), this.groupCategory_selection.includes(i) ? null : 'hidden');

                if (this.groupSources)
                    for (let i = 0; i < this.groupSources.length; i++)
                        appSettings.LocalStorage(this.storageKey + '-groupFilters-type:' + this.groupSources[i].text + '-' + (this.source ? `-${this.source}` : ''), this.groupSources_selection.includes(i) ? null : 'hidden');
            },
            isGroupVisible(category) {
                const selected = this.groupCategory_selection.map(i => this.groupCategories[i].category);
                return selected.some(a => a == category.category);
            },
            isMetricGroupVisible(category) {
                const selected = this.metricCategory_selection.map(i => this.metricCategories[i].category);
                return selected.some(a => a == category.category);
            },
            //getMetricTitle(metric) {
            //    if ((metric.MetricAggs.Type == 'function' || metric.MetricAggs.Type == 'filter' || metric.MetricAggs.Type == 'script' || metric.MetricAggs.Type == 'filter_script') && metric.MetricAggs.FunctionList.length > 1)
            //        return metric.Title;
            //    else if ((metric.MetricAggs.Type == 'function' || metric.MetricAggs.Type == 'filter' || metric.MetricAggs.Type == 'script' || metric.MetricAggs.Type == 'filter_script') && metric.MetricAggs.FunctionList.length == 1) {
            //        const col = this.columnLookup[metric.ColId];
            //        if (col)
            //            return `${metric.Title} (${common.formatMetric({ headerName: col.Title, type: col.SourceType, aggFunc: metric.MetricAggs.FunctionList[0] })})`;
            //        else
            //            return metric.Title;
            //    }
            //    else if (metric.MetricAggs.Type == 'bucket_script')
            //        return metric.Title;
            //    else
            //        return 'N/A';
            //},
            getGroupColumns(category) {
                const visibletypes = this.groupDataTypes_selection.flatMap(i => this.groupDataTypes[i].types);
                const visiblesources = this.groupSources_selection.map(i => this.groupSources[i].source);
                const cols = this.column_list
                    .filter(a => a.ColumnUse.Group && !a.CompoundFilter && a.Category == category.category && visibletypes.includes(a.SourceType) && visiblesources.some(s => a.Sources.includes(s)))
                    .toSorted((a, b) => common.byField(a, b, 'Title'));
                return cols;
            },
            getMetricColumns(category) {
                const visibletypes = this.metricDataTypes_selection.flatMap(i => this.metricDataTypes[i].types);
                const visiblesources = this.metricSources_selection.map(i => this.metricSources[i].source);
                const cols = this.column_list
                    .filter(a => a.ColumnUse.Metric && !a.CompoundFilter && a.Category == category.category && visibletypes.includes(a.SourceType) && a.Sources.some(s => visiblesources.includes(s)))
                    .toSorted((a, b) => common.byField(common.getMetricTitle(a, this.columnLookup), common.getMetricTitle(b, this.columnLookup)));
                return cols;
            },
            setMetricGroupExpansion(category,value){
                this.metricgroup_expanded = { ...this.metricgroup_expanded, [category.category]: value };
            },
            refreshReport: function() {
                console.log('refreshReport groupby:');
                console.log(JSON.stringify(this.groupby,null,3));

                let groupby = this.groupby.map(g => this.groupingColumns.find(c => c.ColId == g)).map(g => ({
                        colId: g.ColId,
                        ...this.groupbysettings[g.ColId],
                    }));

                // Remove the pivot column from the group by
                if (this.pivoton) // && groupby[groupby.length-1].colId == this.pivoton)
                    groupby.splice(groupby.length-1,1);

                const report = {
                    datetime_interval: this.interval_choice,
                    groupby: groupby,
                    metrics: this.metrics.map(m => ({
                        colId: m.ColId,
                        function: m.calculation
                    })),
                    pivoton: this.pivoton ? this.getPivotOn?.ColId : null, // this.pivoton,
                };
                this.notifychange(report);
                //this.$emit('change', report);
            },
            byField(a, b, field) {
                return a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0;
            },

            onDrop(result) {
                // Ex: {"removedIndex":2,"addedIndex":0}
                const item = this.groupby[result.removedIndex];

                this.groupby.splice(result.removedIndex, 1);
                this.groupby.splice(result.addedIndex, 0, item);

                this.notifyGroupOrder();
            },
            notifyGroupOrder() {
                const args = this.groupby.map((a, i) => ({ colId: a == 'datetime' ? 'starttime' : a, toIndex: i }));

                //console.log(`$emit groupbyordered: ${JSON.stringify(args)}`);

                this.$emit('groupbyordered', args);
            },
            onMetricsReorder(newMetrics) {
                this.isReorderingMetrics = true; 
                this.metrics = newMetrics;
                
                // Notify the main grid layout so columns can swap instantly
                this.$emit('metricsordered', newMetrics);
            },

            addMetric(item, calc) {
                this.metrics.push({
                    id: `${calc}(${item.ColId})`,
                    calculation: calc,
                    ...item,
                });
                console.log(`addMetric(${item.ColId}, ${calc})`);
            },
            removeMetric(item, func) {
                if (!func) {
                    const idx = this.metrics.findIndex(a => a.id == item.id);
                    if (idx >= 0)
                        this.metrics.splice(idx, 1);
                }
                else {
                    const idx = this.metrics.findIndex(a => a.ColId == item.ColId && a.calculation == func);
                    if (idx >= 0)
                        this.metrics.splice(idx, 1);
                }
            },
            isDisabledMetric(item, calc) {
                return this.metrics.some(a => a.id == `${calc}(${item.ColId})`);
            },
            isMetricDisabledSource(item) {
                //if (item.ColId == 'datetime' || item.ColId == 'starttime') {
                //    console.log(`${item.ColId}.Sources: ${JSON.stringify(item.Sources)}; groupsources_selected:${JSON.stringify(this.groupsources_selected)}`);
                //    //debugger;
                //}
                if (item.MetricAggs.Type == 'placeholder' && this.metrics.length == 0)
                    return true;

                return !this.groupsources_selected.some(a => item.Sources.includes(a));
            },
            isGroupDisabledSource(item) {
                if (this.groupby.length >= 4 && !this.groupby.includes(item.ColId))
                    return true;

                if (this.metrics.length < 1 || !item.Sources)
                    return false;

                // If the groupby column's sources doesn't have at least one that is part of the metric sources, it is disabled
                return !item.Sources.some(a => this.metricsources_selected.includes(a));
            },
            getMetricHelp(item) {
                return this.Translate(item.Tooltip || item.Title);
            },
            showHelp(item) {
                this.help.item = item;
                this.help.visible = true;
            },
            showMetricFilter(item) {
                this.filter.item = item;
                this.filter.visible = true;
            },
            getMetricItemIcon(item) {
                const mlist = this.metrics.filter(a => a.ColId == item.ColId);
                if (mlist.length > 0) {
                    switch (item.MetricAggs.Type)
                    {
                        case 'function':
                        case 'filter':
                        case 'script':
                        case 'filter_script':
                            if (item.MetricAggs.FunctionList.every(a => mlist.some(f => f.calculation == a)))
                                return 'mdi-checkbox-marked';
                            break;

                        case 'bucket_script':
                            return 'mdi-checkbox-marked';
                    }
                    return 'mdi-checkbox-marked';
                }
                else
                    return 'mdi-checkbox-blank-outline';
            },
            isMetricInUse(item) {
                return this.metrics.some(a => a.ColId == item.ColId);
            },

            toggleGroupby(item) {
                const idx = this.groupby.indexOf(item.ColId);
                if (idx >= 0)
                    this.groupby.splice(idx, 1);
                else
                    this.groupby.push(item.ColId);
            },
            setgroupbysettings(settings) {
                console.log('setgroupsettings:');
                console.log(JSON.stringify(settings,null,3));

                this.groupbysettings = settings;
                this.refreshReport();
            },

            changeColumnGroups(groups) {
                this.columngroups_selection = groups;
            },
            changeMetricGroups(groups) {
                this.columnmetrics_selection = groups;
            },
            changeInterval(interval) {
                this.interval_selection = interval;
            },

            applyDateRangeHandler(range) {
                this.settings.startDate = range.startDate;
                this.settings.endDate = range.endDate;

                this.$emit('daterangechanged');
                //this.System.LocalStorage(this.storageKey + '-daterange', `${common.startDate} - ${common.endDate}`);

                this.refreshReport();
            },

        }
    }
</script>

<style scoped>
    .standard-input-field {
        background-color: white;
    }

    .hidden-element {
        visibility: hidden;
    }

    .columns-two {
        column-count: 2;
    }

    .columns-three {
        column-count: 3;
    }

    .verydense {
        max-height: 24px;
        min-height: 24px;
    }

    .mediumdense {
        max-height: 28px;
        min-height: 28px;
    }

    p {
        font-size: 2em;
        text-align: center;
    }

    .metric_normal {

    }
    .metric_inuse {
        color: cornflowerblue;
    }
    .metric_usedup {
        color: silver;
    }
    .draggable-item {
        cursor: grab;
    }
    .consistentText {
        color: var(--v-black-base) !important; 
        opacity: 0.8 !important; 
        font-size: 14px !important; 
        text-rendering: optimizelegibility !important; 
        letter-spacing:0.1 !important;
    }
    .date-time-toggle :deep(.q-toggle__label) {
        white-space: nowrap;
    }
    .filter-action-btn {
        flex-shrink: 0;
    }
    .filter-action-btn :deep(.q-badge--floating) {
        top: auto;
        bottom: 4px;
    }
</style>


<style>
    .left-pad-4 {
        padding-left: 4px;
    }
    .v-expansion-panel-header__icon{
        margin-left: 0 !important;
    }
</style>